import { API_BASE } from "../config";
// =============================================================================
// File: frontend/src/components/FamilyAllergenGuard.jsx
// NEW FILE - Sprint 2
// Family Allergen Guard - Multi-Profile Safety Checking UI
// CSS EXTRACTED — all styles in healist-app-components.css (fag- prefix)
// Patent claims exercised: P-002 Claims 11-14 (recipe ingredient safety checking)
// =============================================================================

import React, { useState, useEffect } from 'react';
import { CitationChip } from './Citations';

// Common allergens for selection
const COMMON_ALLERGENS = [
  'PEANUT', 'TREE_NUT', 'GLUTEN', 'DAIRY', 'EGG', 'SOY', 
  'FISH', 'SHELLFISH', 'SESAME', 'WHEAT', 'MUSTARD', 'SULFITE', 'CELERY', 'LUPIN', 'CORN', 'MOLLUSKS'
];

const COMMON_INTOLERANCES = [
  'LACTOSE', 'FRUCTOSE', 'HISTAMINE', 'FODMAP', 'GLUTEN'
];

const DIET_RESTRICTIONS = [
  'VEGAN', 'VEGETARIAN', 'KETO', 'PALEO', 'KOSHER', 'HALAL', 'LOW_SODIUM'
];

const RELATIONSHIPS = [
  { value: 'spouse', label: 'Spouse/Partner' },
  { value: 'child', label: 'Child' },
  { value: 'parent', label: 'Parent' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'other', label: 'Other' }
];

const FamilyAllergenGuard = ({ token, fetchWithAuth: fetchWithAuthProp, onClose }) => {
  // State
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('members'); // 'members', 'check', 'add'
  
  // Add/Edit member state
  const [editingMember, setEditingMember] = useState(null);
  const [memberForm, setMemberForm] = useState({
    name: '',
    family_relationship: '',
    date_of_birth: '',
    is_minor: false,
    allergies: [],
    intolerances: [],
    diet_restrictions: [],
    excluded_ingredients: [],
    max_sodium_mg: '',
    max_sugar_g: ''
  });
  
  // Product check state
  const [checkResult, setCheckResult] = useState(null);
  const [checkingProduct, setCheckingProduct] = useState(false);
  const [productToCheck, setProductToCheck] = useState({
    upc: '',
    name: '',
    health_labels: [],
    ingredients_text: ''
  });

  // Fetch helper — delegates to prop fetchWithAuth (with 401 retry) if available
  const fetchWithAuth = fetchWithAuthProp || (async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    return response;
  });

  // Load family members
  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/family/members`);
      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      } else {
        setError('Failed to load family members');
      }
    } catch (err) {
      setError('Network error loading family members');
    } finally {
      setLoading(false);
    }
  };

  // Save member (create or update)
  const saveMember = async () => {
    const payload = {
      ...memberForm,
      family_relationship: memberForm.family_relationship || null,
      date_of_birth: memberForm.date_of_birth || null,
      max_sodium_mg: memberForm.max_sodium_mg ? parseInt(memberForm.max_sodium_mg) : null,
      max_sugar_g: memberForm.max_sugar_g ? parseInt(memberForm.max_sugar_g) : null
    };

    try {
      let response;
      if (editingMember) {
        response = await fetchWithAuth(`${API_BASE}/api/family/members/${editingMember.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetchWithAuth(`${API_BASE}/api/family/members`, {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        await loadMembers();
        resetForm();
        setActiveTab('members');
      } else {
        const err = await response.json();
        const detail = err.detail;
        if (Array.isArray(detail)) {
          setError(detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', '));
        } else {
          setError(detail || 'Failed to save member');
        }
      }
    } catch (err) {
      setError('Network error saving member');
    }
  };

  const deleteMember = async (memberId) => {
    if (!confirm('Remove this family member?')) return;
    
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/family/members/${memberId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        await loadMembers();
      }
    } catch (err) {
      setError('Failed to remove member');
    }
  };

  const resetForm = () => {
    setEditingMember(null);
    setMemberForm({
      name: '',
      family_relationship: '',
      date_of_birth: '',
      is_minor: false,
      allergies: [],
      intolerances: [],
      diet_restrictions: [],
      excluded_ingredients: [],
      max_sodium_mg: '',
      max_sugar_g: ''
    });
  };

  const editMember = (member) => {
    setEditingMember(member);
    setMemberForm({
      name: member.name,
      family_relationship: member.family_relationship || '',
      date_of_birth: member.date_of_birth || '',
      is_minor: member.is_minor,
      allergies: member.allergies || [],
      intolerances: member.intolerances || [],
      diet_restrictions: member.diet_restrictions || [],
      excluded_ingredients: member.excluded_ingredients || [],
      max_sodium_mg: member.max_sodium_mg || '',
      max_sugar_g: member.max_sugar_g || ''
    });
    setActiveTab('add');
  };

  // Toggle array item
  const toggleArrayItem = (field, item) => {
    setMemberForm(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  // Check product against family
  const checkProduct = async () => {
    if (!productToCheck.name && !productToCheck.upc) {
      setError('Enter product name or UPC');
      return;
    }

    setCheckingProduct(true);
    setCheckResult(null);
    setError('');

    try {
      const response = await fetchWithAuth(`${API_BASE}/api/family/check-product`, {
        method: 'POST',
        body: JSON.stringify({
          upc: productToCheck.upc || null,
          product_name: productToCheck.name,
          health_labels: productToCheck.health_labels,
          ingredients_text: productToCheck.ingredients_text
        })
      });

      if (response.ok) {
        const result = await response.json();
        setCheckResult(result);
      } else {
        const err = await response.json();
        setError(err.detail || 'Check failed');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setCheckingProduct(false);
    }
  };

  // Render members list
  const renderMembersList = () => (
    <div>
      {members.length === 0 ? (
        <div className="fag-emptyState">
          <div className="fag-emptyIcon"></div>
          <h3 className="fag-emptyTitle">No Family Members Yet</h3>
          <p className="fag-emptyText">Add family members to check products for everyone's safety.</p>
          <button
            onClick={() => { resetForm(); setActiveTab('add'); }}
            className="fag-primaryButton"
          >
            + Add Family Member
          </button>
        </div>
      ) : (
        <>
          {members.map(member => (
            <div key={member.id} className="fag-memberCard">
              <div className="fag-memberAvatar">
                {(member.name || '?').charAt(0).toUpperCase()}
              </div>
              <div className="fag-memberInfo">
                <div className="fag-memberName">{member.name}</div>
                <div className="fag-memberRelationship">
                  {RELATIONSHIPS.find(r => r.value === member.family_relationship)?.label || 'Family Member'}
                  {member.is_minor && ' • Minor'}
                </div>
                <div className="fag-memberSummary">
                  {(member.allergies || []).length > 0 && (
                    <span className="fag-summaryText">{(member.allergies || []).length} allergies</span>
                  )}
                  {(member.diet_restrictions || []).length > 0 && (
                    <span className="fag-summaryText">{(member.diet_restrictions || []).length} dietary restrictions</span>
                  )}
                </div>
              </div>
              <div className="fag-memberActions">
                <button
                  onClick={() => editMember(member)}
                  className="btn btn-secondary btn-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => deleteMember(member.id)}
                  className="btn btn-danger btn-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
          <button
            onClick={() => { resetForm(); setActiveTab('add'); }}
            className="fag-secondaryButton fag-addAnotherBtn"
          >
            + Add Another Family Member
          </button>
        </>
      )}
    </div>
  );

  // Render add/edit form
  const renderMemberForm = () => (
    <div>
      <h3 className="fag-formTitle">
        {editingMember ? `Edit ${editingMember.name}` : 'Add Family Member'}
      </h3>

      <div className="fag-formRow">
        <div className="fag-formGroup">
          <label className="fag-label">Name *</label>
          <input
            type="text"
            value={memberForm.name}
            onChange={(e) => setMemberForm(prev => ({ ...prev, name: e.target.value }))}
            className="fag-input"
            placeholder="Enter name"
          />
        </div>

        <div className="fag-formGroup">
          <label className="fag-label">Relationship</label>
          <select
            value={memberForm.family_relationship}
            onChange={(e) => setMemberForm(prev => ({ ...prev, family_relationship: e.target.value }))}
            className="fag-select"
          >
            <option value="">Select...</option>
            {RELATIONSHIPS.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="fag-formGroup">
        <label className="fag-label">Food Allergies</label>
        <div className="fag-checkboxGrid">
          {COMMON_ALLERGENS.map(allergen => (
            <label
              key={allergen}
              className={`fag-checkboxLabel${memberForm.allergies.includes(allergen) ? ' fag-checkboxLabel--selected' : ''}`}
            >
              <input
                type="checkbox"
                checked={memberForm.allergies.includes(allergen)}
                onChange={() => toggleArrayItem('allergies', allergen)}
                className="fag-hiddenCheckbox"
              />
              {allergen.replace('_', ' ')}
            </label>
          ))}
        </div>
      </div>

      <div className="fag-formGroup">
        <label className="fag-label">Intolerances</label>
        <div className="fag-checkboxGrid">
          {COMMON_INTOLERANCES.map(item => (
            <label
              key={item}
              className={`fag-checkboxLabel${memberForm.intolerances.includes(item) ? ' fag-checkboxLabel--selected' : ''}`}
            >
              <input
                type="checkbox"
                checked={memberForm.intolerances.includes(item)}
                onChange={() => toggleArrayItem('intolerances', item)}
                className="fag-hiddenCheckbox"
              />
              {item.replace('_', ' ')}
            </label>
          ))}
        </div>
      </div>

      <div className="fag-formGroup">
        <label className="fag-label">Diet Restrictions</label>
        <div className="fag-checkboxGrid">
          {DIET_RESTRICTIONS.map(diet => (
            <label
              key={diet}
              className={`fag-checkboxLabel${memberForm.diet_restrictions.includes(diet) ? ' fag-checkboxLabel--selected' : ''}`}
            >
              <input
                type="checkbox"
                checked={memberForm.diet_restrictions.includes(diet)}
                onChange={() => toggleArrayItem('diet_restrictions', diet)}
                className="fag-hiddenCheckbox"
              />
              {diet.replace('_', ' ')}
            </label>
          ))}
        </div>
      </div>

      <div className="fag-limitsRow">
        <div className="fag-formGroup">
          <label className="fag-label">Max Sodium (mg/day)</label>
          <input
            type="number"
            value={memberForm.max_sodium_mg}
            onChange={(e) => setMemberForm(prev => ({ ...prev, max_sodium_mg: e.target.value }))}
            className="fag-input"
            placeholder="e.g., 1500"
          />
        </div>
        <div className="fag-formGroup">
          <label className="fag-label">Max Sugar (g/day)</label>
          <input
            type="number"
            value={memberForm.max_sugar_g}
            onChange={(e) => setMemberForm(prev => ({ ...prev, max_sugar_g: e.target.value }))}
            className="fag-input"
            placeholder="e.g., 25"
          />
        </div>
      </div>

      <div className="fag-buttonRow">
        <button onClick={() => { resetForm(); setActiveTab('members'); }} className="fag-secondaryButton">
          Cancel
        </button>
        <button onClick={saveMember} className="fag-primaryButton" disabled={!memberForm.name}>
          {editingMember ? 'Save Changes' : 'Add Member'}
        </button>
      </div>
    </div>
  );

  // Render product check
  const renderProductCheck = () => (
    <div>
      <h3 className="fag-checkTitle">Check Product Safety</h3>
      <p className="fag-checkDescription">
        Enter a product to check against all family members' restrictions.
      </p>

      <div className="fag-formGroup">
        <label className="fag-label">Product Name or UPC</label>
        <input
          type="text"
          value={productToCheck.name || productToCheck.upc}
          onChange={(e) => {
            const val = e.target.value;
            if (/^\d+$/.test(val)) {
              setProductToCheck(prev => ({ ...prev, upc: val, name: '' }));
            } else {
              setProductToCheck(prev => ({ ...prev, name: val, upc: '' }));
            }
          }}
          className="fag-input"
          placeholder="Enter product name or scan barcode"
        />
      </div>

      <div className="fag-formGroup">
        <label className="fag-label">Ingredients (optional)</label>
        <textarea
          value={productToCheck.ingredients_text}
          onChange={(e) => setProductToCheck(prev => ({ ...prev, ingredients_text: e.target.value }))}
          className="fag-input fag-textarea"
          placeholder="Paste ingredient list for more accurate checking"
        />
      </div>

      <button
        onClick={checkProduct}
        disabled={checkingProduct || members.length === 0}
        className="fag-primaryButton fag-checkBtn"
        style={{ opacity: (checkingProduct || members.length === 0) ? 0.6 : 1 }}
      >
        {checkingProduct ? 'Checking...' : 'Check Product'}
      </button>

      {members.length === 0 && (
        <p className="fag-noMembersWarning">
          Add family members first to use this feature
        </p>
      )}

      {checkResult && (
        <div className={`fag-resultCard${checkResult.safe_for_all ? ' fag-resultCard--safe' : ' fag-resultCard--unsafe'}`}>
          <div className="fag-resultTitle" style={{ color: checkResult.safe_for_all ? '#065F46' : '#991B1B' }}>
            {checkResult.product_name}: {checkResult.safe_for_all ? 'Safe for Everyone!' : 'Warnings Found'}
          </div>
          <p className="fag-resultSummary">
            {checkResult.summary}
          </p>

          {checkResult.member_results.map(result => (
            <div key={result.member_id} className="fag-memberResult">
              <div className="fag-memberResultName">
                {result.safe ? 'Safe' : 'Warning'} - {result.member_name}
              </div>
              {result.errors.length > 0 && (
                <div className="fag-memberErrors">
                  {result.errors.map((e, i) => <div key={i}>{e}</div>)}
                </div>
              )}
              {result.warnings.length > 0 && (
                <div className="fag-memberWarnings">
                  {result.warnings.map((w, i) => <div key={i}>{w}</div>)}
                </div>
              )}
              {result.safe && result.errors.length === 0 && result.warnings.length === 0 && (
                <div className="fag-memberSafe">No issues found</div>
              )}
              {/* Apple 1.4.1 (G7, 2026-10-17): this member's sources (FALCPA for allergens, the
                  FDA label / NIH source for each food-drug interaction, the guideline for each
                  condition rule). Shown only when there is a finding to cite. */}
              {(result.errors.length > 0 || result.warnings.length > 0) && (
                <CitationChip sources={result.citations} featureName="Family Allergen Check"
                              itemTitle={`${checkResult.product_name} for ${result.member_name}`} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="fag-overlay">
      <div className="fag-modal">
        <div className="fag-header">
          <h2 className="fag-title">
            Family Allergen Guard
          </h2>
          <button className="fag-closeButton" onClick={onClose}>×</button>
        </div>

        <div className="fag-tabs">
          <button
            className={`fag-tab${activeTab === 'members' ? ' fag-tab--active' : ''}`}
            onClick={() => setActiveTab('members')}
          >
            Family Members ({members.length})
          </button>
          <button
            className={`fag-tab${activeTab === 'check' ? ' fag-tab--active' : ''}`}
            onClick={() => setActiveTab('check')}
          >
            Check Product
          </button>
          <button
            className={`fag-tab${activeTab === 'add' ? ' fag-tab--active' : ''}`}
            onClick={() => { resetForm(); setActiveTab('add'); }}
          >
            Add Member
          </button>
        </div>

        <div className="fag-content">
          {error && (
            <div className="fag-error">{error}</div>
          )}

          {loading ? (
            <div className="fag-loading">Loading...</div>
          ) : (
            <>
              {activeTab === 'members' && renderMembersList()}
              {activeTab === 'add' && renderMemberForm()}
              {activeTab === 'check' && renderProductCheck()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FamilyAllergenGuard;